/** External dependencies */
import { Alert, Button, Carousel, Collapse, Dropdown, Modal, ScrollSpy, Tab, Toast } from 'bootstrap';
import * as Handlebars from 'handlebars/runtime';
import * as mapboxgl from 'mapbox-gl';
import Swiper from 'swiper';

/** Internal dependencies */
import { whenDocumentReady } from './utilities/whenDocumentReady';
import {
    bootstrapDetectBreakpoint,
    defineScrollbarWidth,
    getBootstrapBreakpointWidth,
    setHeaderHeight,
    throttle,
} from './utilities';

/**
 * Add bootstrap components you add to the window object here as well
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 5.3.0
 */
interface CtBootstrap {
    Collapse: typeof Collapse;
    Toast: typeof Toast;
}

export type BootstrapBreakpoint = 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

/**
 * These definitions let typescript know what is available globally and how to interpret these things
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 5.3.0
 */
declare global {
    interface Window {
        mapboxgl: typeof mapboxgl;
        Swiper: Swiper;
        bootstrap: CtBootstrap;
        bootstrapDetectBreakpoint: () => BootstrapBreakpoint | null;
        getBootstrapBreakpointWidth: () => number | null;
        Handlebars: Handlebars.RuntimeOptions;
    }
}

// add `Tab` if you need ws forms paging/tabs > it assumes Tab bound to the window.bootstrap object
(window.bootstrap as CtBootstrap) = { Collapse, Toast };
window.bootstrapDetectBreakpoint = bootstrapDetectBreakpoint;
window.getBootstrapBreakpointWidth = getBootstrapBreakpointWidth;

whenDocumentReady(initTheme);

/**
 * JS initializer for our theme. Sets `initialized` class, adds throttled `resize` event, computes header height and scrollbar width and assigns these values to custom CSS properties on the document.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 3.0.0
 * @since 5.2.0 add `initialized` class
 * @since 5.3.0 Implemented in typescript
 */
function initTheme() {
    document.documentElement.style.setProperty(
        '--scrollbar-width',
        window.innerWidth - document.documentElement.clientWidth + 'px'
    );

    throttle('resize', 'optimizedResize');
    window.addEventListener('optimizedResize', defineScrollbarWidth);
    defineScrollbarWidth();
    window.addEventListener('optimizedResize', setHeaderHeight);
    setHeaderHeight();

    document.body.classList.add('initialized');
}

/**
 * Add language information for WS Forms.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 4.0.0
 * @since 5.3.0 Implemented in typescript
 */
if (typeof jQuery !== 'undefined') {
    jQuery(function ($: any) {
        $(document).on('wsf-rendered', function (e: any, form: any, form_id: string, instance_id: any): void {
            const wsForm: HTMLElement = document.querySelector(`.wsf-form[data-id="${form_id}"]`);
            const wsfLang: HTMLInputElement = document.createElement('input');
            wsfLang.type = 'hidden';
            wsfLang.name = 'lang';
            wsfLang.value = 'de';
            wsForm.appendChild(wsfLang);
        });
    });
}

/**
 * Add dynamic infos to wsform
 *
 * @author Kosh <kosh@cubetech.ch>
 * @version 5.3.0
 */
if (typeof jQuery !== 'undefined') {
    jQuery(function ($: any) {
        $(document).on('wsf-rendered', function (e: any, form: any, form_id: string, instance_id: any): void {
            if (form.meta.class_form_wrapper == 'ct-dynamicform') {
                let wsFormElement: HTMLElement = document.querySelector(
                    `.wsf-form[data-id="${form_id}"][data-instance-id="${instance_id}"]`
                );
                if (wsFormElement) {
                    let subject = document.querySelector('h1').innerText;
                    if (subject) {
                        wsFormElement.querySelector('.subject').value = subject;
                        wsFormElement.querySelector('.fld-subject').value = subject;
                    }
                    let overlayId = wsFormElement.closest('.ct_button_overlay').getAttribute('id');
                    if (overlayId) {
                        let buttonElement = document.querySelector('[data-src="#' + overlayId + '"]');
                        if (buttonElement) {
                            let productListingElement = buttonElement.closest('.productlisting');
                            if (productListingElement) {
                                let product = productListingElement.querySelector('h5');
                                if (!product) {
                                    product = productListingElement.querySelector('h4');
                                }
                                if (product) {
                                    wsFormElement.querySelector('.product').value = product.innerText;
                                    wsFormElement.querySelector('.fld-product').value = product.innerText;
                                }
                            }
                        }
                    }
                }
            }
        });
    });
}
